import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// APIs & Hooks
import { baseAxios } from "../apis";
import { useCsrfToken } from "../hooks/useCsrfToken";
import { useToast } from "./toastContext";
import { useAuth } from "./authContext";

// Components & Assets
import DrawerActivateBot from "../components/drawers/DrawerActivateBot";
import DialogSuccess from "../components/dialogs/DialogSuccess";
import DialogAsk from "../components/dialogs/DialogAsk";
import DialogStopBot from "../components/dialogs/DialogStopBot";
import DialogStopBotConvert from "../components/dialogs/DialogStopBotConvert";
import {
  IllustrationBotStop,
  IllustrationConfirmedDocument,
} from "../assets/Illustrations";

// Utils
import { extractErrorMessage } from "../utils";
import useAuthedApi from "../hooks/useAuthedApi";
import {
  useBotDetailQuery,
  useBotListsQuery,
  useConvertDataQuery,
  useExchangesQuery,
  useStrategyListsQuery,
} from "../hooks/useBotQueries";
import { useBotPageFlags } from "../hooks/useBotPageFlags";

// ====================================================================
// 1. QUERY HOOKS & HELPERS (Defined outside the component)
// ====================================================================

export const BOT_STRATEGIES = {
  REBALANCE: "rebalance",
  GRID: "grid",
  XENO_ARC: "xeno arc",
  XENO_V3: "xeno bot v3",
  DCA: "dca",
};

export const BOT_ACTIVATE_STEPS = {
  STEP_1: "step-bot-1",
  STEP_2: "step-bot-2",
  STEP_3: "step-bot-3",
  STEP_2_EDIT: "step-bot-2-edit",
  STEP_3_EDIT: "step-bot-3-edit",
};

// ====================================================================
// 2. THE CONTEXT PROVIDER
// ====================================================================

const BotContext = createContext();

export const BotProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { authedPost, authedDelete } = useAuthedApi();
  const { isPortfolio } = useBotPageFlags();

  const handleApiError = useCallback(
    (error) => {
      const errorMessage = extractErrorMessage(error?.response?.data || error);
      addToast("error", errorMessage, 10000);
    },
    [addToast],
  );

  // --- Client-Side State ---
  const [openDialogBot, setOpenDialogBot] = useState(null);
  const [selectedBot, setSelectedBot] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [activeTabBotList, setActiveTabBotList] = useState("activeBot");

  // --- Data Fetching ---
  const { data: strategyLists = [] } = useStrategyListsQuery();
  const { data: botLists = [], isLoading: loadingBotLists } = useBotListsQuery({
    enabled: !isPortfolio,
  });
  const { data: botDetailData, isLoading: loadingBotDetail } =
    useBotDetailQuery(selectedBot?.id);
  const {
    data: exchangeData,
    isLoading: loadingGetExchange,
    refetch: fetchExchanges,
  } = useExchangesQuery();
  const { data: convertData, isLoading: loadingConvertData } =
    useConvertDataQuery(selectedBot?.id);
  // const { data: botProfileData, isLoading: loadingBotProfile } =
  //   useBotProfileQuery(selectedStrategy?.id);

  // --- MUTATIONS (Defined inside Provider to access state setters) ---

  const stopBotMutation = useMutation({
    mutationFn: ({ botId, method, payload }) =>
      authedPost(`/api/stop_bot/${botId}?method=${method}`, payload),
    onSuccess: async (data, variables) => {
      // First, remove the old bot's detail from the cache
      queryClient.removeQueries({ queryKey: ["botDetail", variables.botId] });

      // Now, wait for the main list to finish refetching
      await queryClient.invalidateQueries({ queryKey: ["botLists"] });

      // Finally, reset the selection. The useEffect will now have the fresh list.
      setSelectedBot(null);
    },
  });

  const startBotMutation = useMutation({
    mutationFn: (botId) => authedPost(`/api/start_bot/${botId}`),
    onSuccess: (data, botId) => {
      queryClient.invalidateQueries({ queryKey: ["botLists"] });
      queryClient.invalidateQueries({ queryKey: ["botDetail", botId] });
    },
  });

  const disconnectExchangeMutation = useMutation({
    mutationFn: (settingId) =>
      authedDelete(`/api/delete_api_setting/${settingId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exchanges"] });
    },
  });

  // --- HANDLERS (Simplified wrappers for mutations) ---
  const handleStopBot = useCallback(
    async (method, payload, callback) => {
      try {
        await stopBotMutation.mutateAsync({
          botId: selectedBot?.id,
          method,
          payload,
        });
        setOpenDialogBot(
          method === "keep"
            ? "success-stop-bot-keep"
            : "success-stop-bot-convert",
        );
        callback?.();
      } catch (err) {
        handleApiError(err);
      }
    },
    [stopBotMutation, selectedBot?.id, handleApiError],
  );

  const handleStartBot = useCallback(async () => {
    try {
      await startBotMutation.mutateAsync(botDetailData?.botDetail?.id);
      setOpenDialogBot("success-start-bot");
    } catch (err) {
      handleApiError(err);
    }
  }, [startBotMutation, botDetailData?.botDetail?.id, handleApiError]);

  const handleDisconnectExchange = useCallback(async () => {
    try {
      const settingId = selectedExchange?.user_api_settings[0]?.id;
      await disconnectExchangeMutation.mutateAsync(settingId);
      setOpenDialogBot("success-disconnect-exchange");
    } catch (err) {
      handleApiError(err);
    }
  }, [disconnectExchangeMutation, selectedExchange, handleApiError]);

  // --- Side Effects for UI Logic ---
  useEffect(() => {
    if (botLists.length > 0 && !selectedBot) {
      setSelectedBot(botLists[0]);
    }
  }, [botLists, selectedBot]);

  useEffect(() => {
    if (botDetailData?.foundStrategy && !openDialogBot) {
      setSelectedStrategy(botDetailData.foundStrategy);
    }
  }, [botDetailData, openDialogBot]);

  // --- Dialogs & Context Value ---
  const dialogsConfig = [
    {
      type: "stop-bot",
      component: DialogStopBot,
      props: {
        handleKeepBalance: () => handleStopBot("keep"),
        botDetail: botDetailData?.botDetail,
      },
    },
    {
      type: "convert",
      component: DialogStopBotConvert,
      props: {
        onBack: () => setOpenDialogBot("stop-bot"),
        handleConvert: (payload, cb) => handleStopBot("convert", payload, cb),
        convertData: convertData,
        loading: loadingConvertData,
        botDetail: botDetailData?.botDetail,
      },
    },
    {
      type: "disconnect-exchange",
      component: DialogAsk,
      props: {
        title:
          "You’re about to disconnect the exchange. Would you like to continue?",
        loading: disconnectExchangeMutation.isPending,
        onConfirm: handleDisconnectExchange,
      },
    },
    {
      type: "start-bot",
      component: DialogAsk,
      props: {
        title: "You’re about to activate this bot. Would you like to continue?",
        loading: startBotMutation.isPending,
        onConfirm: handleStartBot,
      },
    },
    {
      type: "success-disconnect-exchange",
      component: DialogSuccess,
      props: { title: "Success! Your exchange has been disconnected." },
    },
    {
      type: "success-stop-bot-convert",
      component: DialogSuccess,
      props: {
        icon: (
          <IllustrationConfirmedDocument className="w-[300px] h-[300px] my-[-4rem] mt-[-5rem]" />
        ),
        title:
          "Bot stop successful – Your request is being processed. Please check your balance regularly.",
      },
    },
    {
      type: "success-stop-bot-keep",
      component: DialogSuccess,
      props: {
        icon: (
          <IllustrationBotStop className="w-[200px] h-[200px] my-[-2rem] mt-[-3rem]" />
        ),
        title: "Bot stop successful – your bot has been stopped!",
      },
    },
    {
      type: "success-start-bot",
      component: DialogSuccess,
      props: { title: "Bot start successful – your bot has been started!" },
    },
  ];

  // --- Constructing the Context Value ---
  const contextValue = {
    // Data
    strategyLists,
    botLists,
    botDetail: botDetailData?.botDetail || null,
    dataBotProfits: botDetailData?.trends,
    dataChart: botDetailData?.dataChart || [],
    convertData,
    exchanges: exchangeData?.exchanges || [],
    myExchanges: exchangeData?.myExchanges || [],
    // systemResults: botProfileData,

    // Selections & Dialogs
    selectedBot,
    selectedStrategy,
    selectedExchange,
    selectedSetting,
    openDialogBot,
    activeTabBotList,

    // Loading States from React Query
    loadingBotLists,
    loadingBotDetail,
    loadingStopStartBot:
      startBotMutation.isPending || stopBotMutation.isPending,
    loadingConvertData,
    loadingGetExchange,
    // loadingBotProfile,
    loadingDisconnectExchange: disconnectExchangeMutation.isPending,

    // Setters
    setSelectedBot,
    setSelectedStrategy,
    setSelectedExchange,
    setOpenDialogBot,
    setSelectedSetting,
    setActiveTabBotList,
  };

  return (
    <BotContext.Provider value={contextValue}>
      <DrawerActivateBot
        openDialog={openDialogBot}
        botDetail={botDetailData?.botDetail}
        setOpenDialog={setOpenDialogBot}
        setSelectedBot={setSelectedStrategy}
        selectedBot={{ ...selectedStrategy, active_bot_id: selectedBot?.id }}
        bots={strategyLists}
      />
      {dialogsConfig.map(({ type, component: Component, props }) => (
        <Component
          key={type}
          open={openDialogBot === type}
          onClose={() => setOpenDialogBot(null)}
          {...props}
        />
      ))}
      {children}
    </BotContext.Provider>
  );
};

// ====================================================================
// 3. THE CONSUMER HOOK
// ====================================================================

export const useBot = () => {
  const context = useContext(BotContext);
  if (context === undefined) {
    throw new Error("useBot must be used within a BotProvider");
  }
  return context;
};
